// src/pages/CreditTracker/CmeStatePage.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Link,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import IconButton from "@mui/material/IconButton";
import HeaderBar from "../../components/HeaderBar";
import CmeTrackerTour from "../../components/CmeTrackerTour";
import CycleRolloverPreview from "../../components/CycleRolloverPreview";
import { useParams, Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { unslugify } from "../../utils/slugify";
import themeColors from "../../theme";
import axios from "axios";
import config from "../../config";
import { useAuth } from "../../context/AuthContext";
import {
    buildCycleText,
    getCycleDateInputValue,
    getCycleReferenceDateOverride,
    getCycleYears,
    getPersonalDateDescription,
    getPersonalDateFieldLabel,
    getPersonalDateLabel,
    hasGroups,
    isCycleRolloverPreviewEnabled,
    isLicenseBased,
    isLicenseYearAnchored,
    needsPersonalDate,
    requiresLicenseYear,
} from "../../utils/cmeCycle";
import {
    buildCycleHistory, cycleDateString, cycleRangeLabel, getUploadCycle,
} from "../../utils/cmeCycleHistory";

const API_BASE = config.SERVER_URL;
const NON_PHYSICIAN_DESIGNATIONS = ["NP", "PA", "EMS", "EMT"];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
// One-time, user-wide flag: the orientation tour runs only for the FIRST state
// a user opens, regardless of how that state was added (auto from profile or
// manually). Bumping the suffix re-shows it to everyone.
const TOUR_KEY = "creditTracker.onboarded.v1";

export default function CmeStatePage() {
    const { stateSlug } = useParams();
    const navigate = useNavigate();
    const { search } = useLocation();
    const stateName = useMemo(() => unslugify(stateSlug), [stateSlug]);

    const [node, setNode] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [nonPhysicianDialog, setNonPhysicianDialog] = useState(false);
    const [profileChecked, setProfileChecked] = useState(false);

    // First-time orientation tour (-1 = inactive; 0..3 = active step)
    const expirationRef = useRef(null);
    const donutRef = useRef(null);
    const requirementsRef = useRef(null);
    const uploadRef = useRef(null);
    const [tourStep, setTourStep] = useState(-1);
    const [tourRect, setTourRect] = useState(null);
    const tourActive = tourStep >= 0;

    // Upload-related state
    const [form, setForm] = useState({ category: "", credits: "", date: "" });
    const [allRows, setRows] = useState([]);
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [fileError, setFileError] = useState("");
    const [downloadError, setDownloadError] = useState("");
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
    const fileInputRef = useRef(null);
    const cycleDatePrefillAppliedRef = useRef(false);

    // License/birth date prompt
    const [licenseDate, setLicenseDate] = useState(null);
    const [licenseYear, setLicenseYear] = useState(null);
    const [licenseDateSkipped, setLicenseDateSkipped] = useState(false);
    const [licenseDateInput, setLicenseDateInput] = useState("");
    const [licenseYearInput, setLicenseYearInput] = useState("");
    const [licenseDateDialogOpen, setLicenseDateDialogOpen] = useState(false);
    const [licenseDateError, setLicenseDateError] = useState("");   // blocks save
    const [licenseDateWarning, setLicenseDateWarning] = useState(""); // informational
    const [licenseYearError, setLicenseYearError] = useState("");
    const [stateEntryLoaded, setStateEntryLoaded] = useState(false);

    // User's date of birth (from profile) — used to anchor birthday-based cycles (e.g. North Carolina)
    const [birthDate, setBirthDate] = useState(null);
    const [lastName, setLastName] = useState("");

    // Group selection (e.g. Florida MD)
    const [licenseGroup, setLicenseGroup] = useState(null);
    const [licenseGroupInput, setLicenseGroupInput] = useState("");
    const [groupDialogOpen, setGroupDialogOpen] = useState(false);

    // Expiration date
    const [expirationDate, setExpirationDate] = useState(null);
    const [expirationDateInput, setExpirationDateInput] = useState("");
    const [expirationDateEditing, setExpirationDateEditing] = useState(false);

    const { token } = useAuth();
    const [today, setToday] = useState(() => cycleDateString(new Date()));
    const todayStr = today;
    // Also roll over a tracker left open overnight or resumed after sleeping.
    useEffect(() => {
        const refreshDay = () => setToday(cycleDateString(new Date()));
        const timer = window.setInterval(refreshDay, 60000);
        window.addEventListener("focus", refreshDay);
        document.addEventListener("visibilitychange", refreshDay);
        return () => {
            window.clearInterval(timer);
            window.removeEventListener("focus", refreshDay);
            document.removeEventListener("visibilitychange", refreshDay);
        };
    }, []);
    // Deployed QA: append ?dev=true&cycleDate=2027-01-01 to a state URL.
    const cycleReferenceDateOverride = useMemo(
        () => getCycleReferenceDateOverride(search, new Date(`${today}T00:00:00`)),
        [search, today]
    );
    const cycleReferenceDate = useMemo(
        () => cycleReferenceDateOverride || new Date(`${today}T00:00:00`),
        [cycleReferenceDateOverride, today]
    );
    const authHeaders = useMemo(
        () => (token ? { Authorization: `Bearer ${token}` } : {}),
        [token]
    );

    // Check if user has a non-physician designation
    useEffect(() => {
        if (!token) return;
        axios
            .get(`${API_BASE}/api/profile`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(({ data }) => {
                if (data?.designation && NON_PHYSICIAN_DESIGNATIONS.includes(data.designation)) {
                    setNonPhysicianDialog(true);
                }
            })
            .catch(() => {})
            .finally(() => setProfileChecked(true));
    }, [token]);

    // Fetch CME requirements from backend
    useEffect(() => {
        const fetchState = async () => {
            try {
                setLoading(true);
                const { data } = await axios.get(`${API_BASE}/api/cme/requirements/${stateSlug}`, {
                    headers: authHeaders
                });
                setNode(data);
            } catch (e) {
                setErr(e?.response?.data?.message || "Failed to load state");
            } finally {
                setLoading(false);
            }
        };
        fetchState();
    }, [stateSlug, authHeaders]);

    // Fetch user's state entry (for licenseDate)
    useEffect(() => {
        const fetchStateEntry = async () => {
            try {
                const { data } = await axios.get(`${API_BASE}/api/cmeStates/${stateSlug}`, {
                    headers: authHeaders
                });
                if (data?.licenseDate) {
                    setLicenseDate(data.licenseDate);
                }
                if (data?.licenseYear) {
                    setLicenseYear(data.licenseYear);
                }
                if (data?.licenseDateSkipped) {
                    setLicenseDateSkipped(true);
                }
                if (data?.licenseGroup) {
                    setLicenseGroup(data.licenseGroup);
                }
                if (data?.expirationDate) {
                    setExpirationDate(data.expirationDate);
                }
            } catch (e) {
                // 404 is fine — user may not have added this state yet
            } finally {
                setStateEntryLoaded(true);
            }
        };
        fetchStateEntry();
    }, [stateSlug, authHeaders]);

    // Fetch user's date of birth (for birthday-anchored cycles like North Carolina)
    useEffect(() => {
        const fetchProfileDob = async () => {
            try {
                const { data } = await axios.get(`${API_BASE}/api/profile`, {
                    headers: authHeaders,
                });
                if (data?.dob) setBirthDate(String(data.dob).slice(0, 10));
                if (data?.lastName) setLastName(data.lastName);
            } catch (e) {
                // No profile / no dob — birthday-anchored cycles will stay blank
            }
        };
        fetchProfileDob();
    }, [authHeaders]);

    // Prefill birth-based cycle prompts from the profile without preventing the
    // user from clearing or replacing the value after the dialog opens.
    useEffect(() => {
        if (!licenseDateDialogOpen) {
            cycleDatePrefillAppliedRef.current = false;
            return;
        }
        if (cycleDatePrefillAppliedRef.current || licenseDateInput) return;

        const prefill = getCycleDateInputValue(node?.cycle, licenseDate, birthDate);
        if (!prefill) return;

        setLicenseDateInput(prefill);
        cycleDatePrefillAppliedRef.current = true;
    }, [licenseDateDialogOpen, licenseDateInput, node, licenseDate, birthDate]);

    // Show license date prompt if needed and not yet stored or skipped.
    // Suppressed while the orientation tour is active so the two don't collide;
    // once the tour ends, `tourActive` flips and this re-evaluates.
    useEffect(() => {
        if (!node || !stateEntryLoaded) return;
        if (tourActive) return;
        // Group-based cycle (e.g. Florida MD)
        if (hasGroups(node.cycle) && !licenseGroup) {
            setGroupDialogOpen(true);
            return;
        }
        if (licenseDateSkipped) return;
        if (!needsPersonalDate(node.cycle)) return;
        const hasDate = !!licenseDate;
        const hasYear = !requiresLicenseYear(node.cycle) || !!licenseYear;
        if (hasDate && hasYear) return;
        setLicenseDateDialogOpen(true);
    }, [node, stateEntryLoaded, licenseDate, licenseYear, licenseDateSkipped, licenseGroup, tourActive]);

    // Start the one-time orientation tour for the first state opened.
    useEffect(() => {
        if (loading || !node || !profileChecked) return;
        if (nonPhysicianDialog) return; // non-physicians get the blocking dialog instead
        if (tourStep !== -1) return;
        if (localStorage.getItem(TOUR_KEY) === "true") return;
        setTourStep(0);
    }, [loading, node, profileChecked, nonPhysicianDialog, tourStep]);

    // Track the spotlight target for the active tour step (steps 0–2).
    useEffect(() => {
        if (!tourActive || tourStep === 3) return;
        const ref = [expirationRef, donutRef, requirementsRef][tourStep];
        const measure = () => {
            if (ref?.current) setTourRect(ref.current.getBoundingClientRect());
        };
        if (ref?.current) ref.current.scrollIntoView({ block: "center", behavior: "smooth" });
        measure();
        window.addEventListener("resize", measure);
        window.addEventListener("scroll", measure, true);
        return () => {
            window.removeEventListener("resize", measure);
            window.removeEventListener("scroll", measure, true);
        };
    }, [tourActive, tourStep]);

    const finishTour = useCallback(() => {
        localStorage.setItem(TOUR_KEY, "true");
        setTourStep(-1);
    }, []);

    const handleTourNext = useCallback(() => {
        setTourStep((prev) => (prev >= 3 ? prev : prev + 1));
    }, []);

    const handleTourSaveExpiration = useCallback(async (value) => {
        if (!value) return;
        try {
            await axios.patch(
                `${API_BASE}/api/cmeStates/${stateSlug}`,
                { expirationDate: value },
                { headers: authHeaders }
            );
            setExpirationDate(value);
        } catch (e) {
            console.error("Failed to save expiration date:", e);
        }
    }, [stateSlug, authHeaders]);

    const handleTourSelectCategory = useCallback((category) => {
        if (category) setForm((f) => ({ ...f, category }));
        finishTour();
        if (uploadRef.current) {
            uploadRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
        }
    }, [finishTour]);

    const handleLicenseDateChange = (e) => {
        const val = e.target.value;
        setLicenseDateInput(val);
        setLicenseDateError("");
        setLicenseDateWarning("");

        if (!val) return;

        const entered = new Date(val + "T00:00:00");
        if (isNaN(entered)) return;

        // Block future dates
        if (entered > new Date()) {
            setLicenseDateError("Date cannot be in the future.");
            return;
        }

        // Warn if license date is too old (more than one cycle length ago).
        // Anchored cycles keep rolling from the original issue date.
        if (isLicenseBased(node?.cycle) && !node?.cycle?.birthday_anchored && !isLicenseYearAnchored(node?.cycle)) {
            const years = getCycleYears(node.cycle);
            if (years) {
                const cutoff = new Date();
                cutoff.setFullYear(cutoff.getFullYear() - years);
                if (entered < cutoff) {
                    setLicenseDateWarning(
                        `Your license was issued more than ${years} year${years > 1 ? "s" : ""} ago. Please verify you entered the date correctly, otherwise your license may be expired.`
                    );
                }
            }
        }
    };

    const handleLicenseYearChange = (e) => {
        const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);
        setLicenseYearInput(val);
        setLicenseYearError("");
        if (!val) return;
        if (val.length < 4) return;
        const yr = parseInt(val, 10);
        const currentYear = new Date().getFullYear();
        if (yr > currentYear) {
            setLicenseYearError("Year cannot be in the future.");
        } else if (yr < 1950) {
            setLicenseYearError("Please enter a valid year.");
        }
    };

    const handleSaveLicenseDate = async () => {
        if (!licenseDateInput || licenseDateError) return;
        const needsYear = requiresLicenseYear(node?.cycle);
        if (needsYear && (!licenseYearInput || licenseYearInput.length !== 4 || licenseYearError)) return;
        try {
            const payload = { licenseDate: licenseDateInput };
            if (needsYear) payload.licenseYear = parseInt(licenseYearInput, 10);
            await axios.patch(
                `${API_BASE}/api/cmeStates/${stateSlug}`,
                payload,
                { headers: authHeaders }
            );
            setLicenseDate(licenseDateInput);
            if (needsYear) setLicenseYear(parseInt(licenseYearInput, 10));
            setLicenseDateSkipped(false);
            setLicenseDateDialogOpen(false);
        } catch (e) {
            console.error("Failed to save license date:", e);
        }
    };

    const handleSkipLicenseDate = async () => {
        try {
            await axios.patch(
                `${API_BASE}/api/cmeStates/${stateSlug}`,
                { licenseDateSkipped: true },
                { headers: authHeaders }
            );
        } catch (e) {
            console.error("Failed to save skip:", e);
        }
        setLicenseDateSkipped(true);
        setLicenseDateDialogOpen(false);
    };

    const handleSaveGroup = async () => {
        if (!licenseGroupInput) return;
        try {
            await axios.patch(
                `${API_BASE}/api/cmeStates/${stateSlug}`,
                { licenseGroup: licenseGroupInput },
                { headers: authHeaders }
            );
            setLicenseGroup(licenseGroupInput);
            setGroupDialogOpen(false);
        } catch (e) {
            console.error("Failed to save group:", e);
        }
    };

    const handleSaveExpirationDate = async () => {
        try {
            await axios.patch(
                `${API_BASE}/api/cmeStates/${stateSlug}`,
                { expirationDate: expirationDateInput || null },
                { headers: authHeaders }
            );
            setExpirationDate(expirationDateInput || null);
            setExpirationDateEditing(false);
        } catch (e) {
            console.error("Failed to save expiration date:", e);
        }
    };

    // Fetch uploads
    const fetchUploads = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API_BASE}/api/cmeUploads`, {
                params: { stateSlug },
                headers: authHeaders
            });
            setRows(
                (data || []).map((d) => ({
                    id: d._id,
                    dateRaw: d.date,
                    cycleStart: d.cycleStart,
                    cycleEnd: d.cycleEnd,
                    date: new Date(`${String(d.date).slice(0, 10)}T00:00:00`).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric"
                    }),
                    credits: Number(d.credits || 0),
                    category: d.category,
                    url: d.sasUrl, // SAS from backend
                }))
            );
        } catch (e) {
            console.error("Failed to load uploads:", e);
        }
    }, [stateSlug, authHeaders]);

    useEffect(() => {
        fetchUploads();
    }, [fetchUploads]);

    const monthYearOnly = node?.state === "Ohio";
    const cycleText = useMemo(
        () => buildCycleText(node?.cycle, licenseDate, licenseGroup, licenseYear, birthDate, {
            lastName,
            referenceDate: cycleReferenceDate,
            monthYearOnly,
            expirationDate,
        }),
        [node, licenseDate, licenseGroup, licenseYear, birthDate, lastName, cycleReferenceDate, monthYearOnly, expirationDate]
    );

    const cycleOptions = useMemo(() => ({
        personalDateStr: licenseDate, groupLabel: licenseGroup, licenseYear,
        birthDate, lastName, expirationDate, referenceDate: cycleReferenceDate,
    }), [licenseDate, licenseGroup, licenseYear, birthDate, lastName, expirationDate, cycleReferenceDate]);
    const cycleHistory = useMemo(
        () => buildCycleHistory(allRows, node?.cycle, cycleOptions),
        [allRows, node, cycleOptions]
    );
    const requestedCycle = new URLSearchParams(search).get("cycle");
    const priorCycle = cycleHistory.previous.find((cycle) => cycle.key === requestedCycle);
    const rows = priorCycle ? priorCycle.rows : cycleHistory.currentRows;
    const displayedCycle = priorCycle || cycleHistory.current;
    const selectCycle = (key) => {
        const params = new URLSearchParams(search);
        if (key) params.set("cycle", key);
        else params.delete("cycle");
        navigate({ search: params.toString() ? `?${params}` : "" });
    };

    // ✅ Compute per-requirement progress from uploaded rows (including General)
    const { reqCards, totalRequired, totalCompleted } = useMemo(
        () => mapRequirementsWithProgress(node?.requirements || [], rows),
        [node, rows]
    );

    // Notices shown as info icon(s) next to the "Requirements" heading.
    // Supports an array (`requirements_notices`) or a single string (`requirements_notice`).
    const reqNotices = useMemo(() => {
        if (Array.isArray(node?.requirements_notices)) return node.requirements_notices.filter(Boolean);
        if (node?.requirements_notice) return [node.requirements_notice];
        return [];
    }, [node]);

    // Dropdown categories (all requirement names, which now includes "General CME")
    const categories = useMemo(() => {
        const reqNames = (node?.requirements || []).map((r) => r.name).filter(Boolean);
        // Remove duplicates and ensure "General CME" appears first if it exists
        const uniqueNames = Array.from(new Set(reqNames));
        const generalIndex = uniqueNames.findIndex(name => norm(name).startsWith(norm("General CME")));
        if (generalIndex > 0) {
            // Move "General CME" to the front
            const [general] = uniqueNames.splice(generalIndex, 1);
            uniqueNames.unshift(general);
        }
        return uniqueNames;
    }, [node]);

    // Donut progress: sum of all uploaded credits
    const creditsCompleted = totalCompleted;
    const defaultCategory = categories[0] || "";

    useEffect(() => {
        setForm((prev) => {
            if (!categories.length) return prev;
            if (prev.category && categories.includes(prev.category)) return prev;
            return { ...prev, category: defaultCategory };
        });
    }, [categories, defaultCategory]);

    const handleChange = (k) => (e) => {
        const value = e.target.value;
        // Prevent negative numbers for credits
        if (k === "credits" && value !== "" && Number(value) < 0) {
            return; // Don't update if negative
        }
        setForm((f) => ({ ...f, [k]: value }));
    };

    // File handling
    const onFileChange = (e) => {
        const f = e.target.files?.[0];
        if (f) {
            if (f.size > MAX_FILE_SIZE) {
                setFileError("File is too large. Maximum file size is 5MB.");
                setFile(null);
            } else {
                setFileError("");
                setFile(f);
            }
        }
    };

    const openFilePicker = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
            fileInputRef.current.click();
        }
    };
    const handleDrop = (e) => {
        e.preventDefault();
        const f = e.dataTransfer?.files?.[0];
        if (f) {
            if (f.size > MAX_FILE_SIZE) {
                setFileError("File is too large. Maximum file size is 5MB.");
                setFile(null);
            } else {
                setFileError("");
                setFile(f);
            }
        }
    };
    const handleDragOver = (e) => e.preventDefault();

    const handleUpload = async () => {
        if (!file || !form.category || !form.credits || !form.date) return;
        // Validate credits is not negative
        const creditsNum = Number(form.credits);
        if (isNaN(creditsNum) || creditsNum < 0) {
            alert("Credits must be a non-negative number");
            return;
        }
        if (form.date > todayStr) {
            alert("Date cannot be in the future");
            return;
        }
        try {
            setUploading(true);
            const fd = new FormData();
            fd.append("file", file);
            fd.append("stateSlug", stateSlug);
            fd.append("category", form.category);
            fd.append("credits", String(form.credits));
            fd.append("date", form.date);
            const uploadCycle = getUploadCycle({ dateRaw: form.date }, node?.cycle, cycleOptions);
            if (uploadCycle) {
                fd.append("cycleStart", cycleDateString(uploadCycle.start));
                fd.append("cycleEnd", cycleDateString(uploadCycle.end));
            }

            await axios.post(`${API_BASE}/api/cmeUploads`, fd, {
                headers: {
                    ...authHeaders,
                    "Content-Type": "multipart/form-data"
                }
            });

            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            setForm({ category: defaultCategory, credits: "", date: "" });
            await fetchUploads(); // refresh -> recalculates progress
        } catch (e) {
            console.error("Upload failed:", e);
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteConfirmed = async () => {
        try {
            await axios.delete(`${API_BASE}/api/cmeUploads/${deleteConfirm.id}`, { headers: authHeaders });
            await fetchUploads(); // refresh -> recalculates progress
            setDeleteConfirm({ open: false, id: null });
        } catch (e) {
            console.error("Delete failed:", e);
        }
    };

    const handleDownload = async (event, row) => {
        event.preventDefault();
        setDownloadError("");
        // Open during the user gesture so browsers don't block the async result.
        const downloadWindow = window.open("about:blank", "_blank");
        if (downloadWindow) downloadWindow.opener = null;
        try {
            const { data } = await axios.get(`${API_BASE}/api/cmeUploads/${row.id}/download`, {
                headers: authHeaders,
            });
            if (!data.url) throw new Error("Download URL unavailable");
            if (downloadWindow) downloadWindow.location.replace(data.url);
            else window.location.assign(data.url);
        } catch (error) {
            downloadWindow?.close();
            setDownloadError("Unable to download the certificate. Please try again.");
        }
    };

    // Loading / error UI
    if (loading) {
        return (
            <>
                <HeaderBar />
                <Box sx={{ maxWidth: 1100, mx: "auto", py: 4 }}>
                    <Typography variant="h6">Loading…</Typography>
                </Box>
            </>
        );
    }

    if (err || !node) {
        return (
            <>
                <HeaderBar />
                <Box sx={{ maxWidth: 1100, mx: "auto", py: 4 }}>
                    <Typography variant="h5" sx={{ mb: 1 }}>
                        {stateName}
                    </Typography>
                    <Typography sx={{ mb: 2 }}>{err || "State not found."}</Typography>
                    <Button component={RouterLink} to="/creditTracker" variant="outlined">
                        ← Back
                    </Button>
                </Box>
            </>
        );
    }

    return (
        <>
            <HeaderBar />
            <Box sx={{ maxWidth: 1100, mx: "auto", py: 4 }}>
                {/* Title + Cycle */}
                <Typography variant="h4" sx={{ mb: 0.5 }}>
                    {node.state} CME Tracker
                </Typography>
                {node.cycle && isCycleRolloverPreviewEnabled(search) && (
                    <CycleRolloverPreview
                        referenceDate={cycleReferenceDateOverride}
                        currentCycle={cycleHistory.current}
                        previousCycle={cycleHistory.previous[0]}
                    />
                )}
                {node.cycle && <Box sx={{ bgcolor: "#f0f7ff", border: "1px solid #dceaff", borderRadius: 2, p: 2, mb: 2 }}>
                    <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "center" }} justifyContent="space-between" spacing={2}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <CalendarMonthOutlinedIcon sx={{ color: "#2455b5", fontSize: 32 }} />
                            <Box>
                                <Typography variant="body2">{priorCycle ? "Prior Cycle" : "Current Cycle"}</Typography>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Typography sx={{ fontWeight: 700 }}>
                                        {displayedCycle ? cycleRangeLabel(displayedCycle, false, monthYearOnly)
                                            : node.cycle.renewal_date_anchored ? "Enter your license expiration date below to determine your cycle"
                                                : cycleText || "Enter your cycle details"}
                                    </Typography>
                                    {!priorCycle && (hasGroups(node?.cycle) || needsPersonalDate(node?.cycle)) && (
                                        <Tooltip title={hasGroups(node?.cycle) ? "Change group" : "Edit cycle date"} arrow>
                                            <IconButton size="small" onClick={() => {
                                                if (hasGroups(node?.cycle)) {
                                                    setLicenseGroupInput(licenseGroup || "");
                                                    setGroupDialogOpen(true);
                                                } else {
                                                    setLicenseDateInput(getCycleDateInputValue(node?.cycle, licenseDate, birthDate));
                                                    setLicenseYearInput(licenseYear ? String(licenseYear) : "");
                                                    setLicenseDateError("");
                                                    setLicenseDateWarning("");
                                                    setLicenseYearError("");
                                                    setLicenseDateDialogOpen(true);
                                                }
                                            }}>
                                                <EditOutlinedIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Stack>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    {priorCycle
                                        ? "Saved certificates and progress for this completed cycle."
                                        : "This is your active cycle. Progress and requirements are based on these dates."}
                                </Typography>
                            </Box>
                        </Stack>
                        {cycleHistory.previous.length > 0 && (
                            <TextField select size="small" label="View Previous Cycles" value={priorCycle?.key || ""}
                                onChange={(event) => selectCycle(event.target.value)} sx={{ minWidth: 280 }}>
                                <MenuItem value="">Back to current cycle</MenuItem>
                                {cycleHistory.previous.map((cycle) => (
                                    <MenuItem key={cycle.key} value={cycle.key}>
                                        {cycleRangeLabel(cycle, true, monthYearOnly)}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    </Stack>
                    {priorCycle && <Button size="small" onClick={() => selectCycle("")} sx={{ mt: 1 }}>← Back to current cycle</Button>}
                </Box>}
                {node.cycle && cycleReferenceDateOverride && (
                    <Typography variant="caption" sx={{ display: "block", mb: 2, color: "warning.main" }}>
                        Cycle test date: {cycleReferenceDateOverride.toLocaleDateString("en-US", {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                        })}
                    </Typography>
                )}

                {/* Expiration Date */}
                <Box ref={expirationRef} sx={{ mb: 2 }}>
                    {!priorCycle && expirationDateEditing ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                License Expiration Date:
                            </Typography>
                            <TextField
                                type="date"
                                size="small"
                                label="License expiration date"
                                value={expirationDateInput}
                                onChange={(e) => setExpirationDateInput(e.target.value)}
                                slotProps={{ inputLabel: { shrink: true } }}
                                sx={{ width: 180 }}
                            />
                            <Button
                                size="small"
                                variant="contained"
                                onClick={handleSaveExpirationDate}
                                sx={{
                                    textTransform: "none",
                                    backgroundColor: themeColors.teal,
                                    "&:hover": { backgroundColor: themeColors.darkTeal },
                                }}
                            >
                                Save
                            </Button>
                            <Button
                                size="small"
                                onClick={() => {
                                    setExpirationDateEditing(false);
                                    setExpirationDateInput(expirationDate || "");
                                }}
                                sx={{ textTransform: "none" }}
                            >
                                Cancel
                            </Button>
                        </Stack>
                    ) : (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                License Expiration Date:{" "}
                                <Typography component="span" variant="body2" sx={{ fontWeight: 400 }}>
                                    {expirationDate
                                        ? new Date(expirationDate + "T00:00:00").toLocaleDateString("en-US", {
                                              month: "long",
                                              day: "numeric",
                                              year: "numeric",
                                          })
                                        : "Not set"}
                                </Typography>
                            </Typography>
                            {!priorCycle && <Tooltip title={expirationDate ? "Edit expiration date" : "Enter expiration date"} arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        setExpirationDateInput(expirationDate || "");
                                        setExpirationDateEditing(true);
                                    }}
                                    sx={{ color: "#999" }}
                                >
                                    <EditOutlinedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>}
                        </Stack>
                    )}
                </Box>

                {/* Donut + Requirements */}
                <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e8eaed", mb: 3 }}>
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                        <Grid container spacing={3} sx={{ alignItems: "center", justifyContent: "center" }}>
                            <Grid
                                ref={donutRef}
                                size={{ xs: 12, md: "auto" }}
                                sx={{
                                    flexShrink: 0,
                                    display: "flex",
                                    justifyContent: "center",
                                    pr: { md: 4, lg: 6 }
                                }}
                            >
                                <DonutProgress completed={creditsCompleted} total={totalRequired} />
                            </Grid>

                            <Grid ref={requirementsRef} size={{ xs: 12, md: "grow" }}>
                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
                                    <Typography variant="subtitle1">Requirements</Typography>
                                    {reqNotices.map((notice, i) => (
                                        <Tooltip key={i} title={notice} arrow placement="top">
                                            <InfoOutlinedIcon sx={{ fontSize: 16, color: "#999", cursor: "pointer" }} />
                                        </Tooltip>
                                    ))}
                                </Stack>
                                <Stack spacing={1.2}>
                                    {reqCards.map((r, i) => (
                                        <RequirementCard key={i} req={r} />
                                    ))}
                                </Stack>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Upload panel */}
                <Card ref={uploadRef} elevation={0} sx={{ borderRadius: 3, border: "1px solid #e8eaed" }}>
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                        {!priorCycle && <>
                        <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                            Upload CME Certificate
                        </Typography>

                        <Box
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onClick={openFilePicker}
                            sx={{
                                border: "2px dashed #d9dde1",
                                borderRadius: 2,
                                p: 3,
                                textAlign: "center",
                                color: "#7b8794",
                                mb: 2,
                                cursor: "pointer"
                            }}
                        >
                            {file ? `Selected: ${file.name}` : "Drag and drop a file here or click to upload"}
                            <input
                                id="cme-file-input"
                                ref={fileInputRef}
                                type="file"
                                style={{ display: "none" }}
                                onChange={onFileChange}
                            />
                        </Box>

                        {fileError && (
                            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                                {fileError}
                            </Typography>
                        )}

                        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
                            <TextField select value={form.category} onChange={handleChange("category")} fullWidth>
                                {categories.map((c) => (
                                    <MenuItem key={c} value={c}>
                                        {c}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                label="Credits"
                                type="number"
                                value={form.credits}
                                onChange={handleChange("credits")}
                                inputProps={{ min: 0, step: 0.1 }}
                                sx={{ width: 160 }}
                                error={form.credits !== "" && Number(form.credits) < 0}
                                helperText={form.credits !== "" && Number(form.credits) < 0 ? "Credits cannot be negative" : ""}
                            />
                            <TextField
                                label="mm/dd/yyyy"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={form.date}
                                onChange={handleChange("date")}
                                sx={{ width: 200 }}
                                inputProps={{ max: todayStr }}
                                error={!!form.date && form.date > todayStr}
                                helperText={!!form.date && form.date > todayStr ? "Date cannot be in the future" : ""}
                            />
                        </Stack>

                        <Button
                            variant="contained"
                            onClick={handleUpload}
                            disabled={uploading || !file || !form.category || !form.credits || !form.date}
                            sx={{
                                backgroundColor: themeColors.teal,
                                textTransform: "none",
                                fontWeight: 600,
                                "&:hover": { backgroundColor: themeColors.darkTeal }
                            }}
                        >
                            {uploading ? "Uploading..." : "Upload"}
                        </Button>

                        {node.cycle && <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                            Certificates are saved to the cycle containing their completion date.
                        </Typography>}
                        </>}
                        {priorCycle && <Typography variant="subtitle1">Saved CME Certificates</Typography>}

                        {/* Table of uploaded files */}
                        <Box sx={{ mt: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Credits</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Download</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {downloadError && <TableRow><TableCell colSpan={5} role="alert">{downloadError}</TableCell></TableRow>}
                                    {rows.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5}>No uploads yet.</TableCell>
                                        </TableRow>
                                    ) : (
                                        rows.map((r) => (
                                            <TableRow key={r.id}>
                                                <TableCell>{r.date}</TableCell>
                                                <TableCell>{r.credits}</TableCell>
                                                <TableCell>{r.category}</TableCell>
                                                <TableCell>
                                                    {r.url ? (
                                                        <Link href={r.url} target="_blank" rel="noreferrer" onClick={(event) => handleDownload(event, r)}>
                                                            Download
                                                        </Link>
                                                    ) : (
                                                        "—"
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {!priorCycle && <Button
                                                        size="small"
                                                        color="error"
                                                        onClick={() => setDeleteConfirm({ open: true, id: r.id })}
                                                    >
                                                        Delete
                                                    </Button>}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    </CardContent>
                </Card>

                {/* DELETE CONFIRMATION DIALOG */}
                <Dialog
                    open={deleteConfirm.open}
                    onClose={() => setDeleteConfirm({ open: false, id: null })}
                >
                    <DialogTitle>Confirm Deletion</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to delete this upload? This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteConfirm({ open: false, id: null })}>
                            Cancel
                        </Button>
                        <Button color="error" onClick={handleDeleteConfirmed}>
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* LICENSE / BIRTH DATE PROMPT DIALOG */}
                <Dialog
                    open={licenseDateDialogOpen}
                    onClose={() => setLicenseDateDialogOpen(false)}
                >
                    <DialogTitle>
                        {getPersonalDateLabel(node?.cycle)}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ mb: 2 }}>
                            {getPersonalDateDescription(node?.state, node?.cycle)}
                        </DialogContentText>
                        <TextField
                            type="date"
                            label={getPersonalDateFieldLabel(node?.cycle)}
                            fullWidth
                            value={licenseDateInput}
                            onChange={handleLicenseDateChange}
                            slotProps={{
                                inputLabel: { shrink: true },
                                htmlInput: { max: todayStr },
                            }}
                            error={!!licenseDateError}
                            helperText={licenseDateError}
                        />
                        {licenseDateWarning && (
                            <Typography variant="body2" sx={{ mt: 1.5, color: "warning.main" }}>
                                {licenseDateWarning}
                            </Typography>
                        )}
                        {requiresLicenseYear(node?.cycle) && (
                            <TextField
                                label="License Issue Year"
                                fullWidth
                                value={licenseYearInput}
                                onChange={handleLicenseYearChange}
                                placeholder="e.g. 2024"
                                inputProps={{ inputMode: "numeric", maxLength: 4 }}
                                error={!!licenseYearError}
                                helperText={licenseYearError || "The year your medical license was first issued in this state."}
                                sx={{ mt: 2 }}
                            />
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleSkipLicenseDate}>
                            Skip
                        </Button>
                        <Button
                            variant="contained"
                            disabled={
                                !licenseDateInput ||
                                !!licenseDateError ||
                                (requiresLicenseYear(node?.cycle) && (!licenseYearInput || licenseYearInput.length !== 4 || !!licenseYearError))
                            }
                            onClick={handleSaveLicenseDate}
                            sx={{
                                backgroundColor: themeColors.teal,
                                "&:hover": { backgroundColor: themeColors.darkTeal },
                            }}
                        >
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Non-Physician Designation Dialog */}
                <Dialog open={nonPhysicianDialog} onClose={() => navigate("/dashboard")} maxWidth="xs" fullWidth>
                    <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <InfoOutlinedIcon sx={{ color: themeColors.teal }} />
                        Credit Tracker
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            At this time, Credit Tracker is only available for physicians. We hope to include CME credit tracker for other providers in the future.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => navigate("/dashboard")}
                            variant="contained"
                            sx={{
                                backgroundColor: themeColors.teal,
                                "&:hover": { backgroundColor: themeColors.darkTeal },
                            }}
                        >
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* GROUP SELECTION DIALOG (e.g. Florida MD) */}
                <Dialog
                    open={groupDialogOpen}
                    onClose={() => { if (licenseGroup) setGroupDialogOpen(false); }}
                >
                    <DialogTitle>Select Your License Group</DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ mb: 2 }}>
                            Please select which group your license is assigned to. Group 1 or Group 2
                        </DialogContentText>
                        <TextField
                            select
                            fullWidth
                            value={licenseGroupInput}
                            onChange={(e) => setLicenseGroupInput(e.target.value)}
                            label="Select Group"
                        >
                            {(node?.cycle?.groups || []).map((g) => (
                                <MenuItem key={g.label} value={g.label}>
                                    {g.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="contained"
                            disabled={!licenseGroupInput}
                            onClick={handleSaveGroup}
                            sx={{
                                backgroundColor: themeColors.teal,
                                "&:hover": { backgroundColor: themeColors.darkTeal },
                            }}
                        >
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* FIRST-TIME ORIENTATION TOUR (once per user, first state opened) */}
                {tourActive && (
                    <CmeTrackerTour
                        stepIndex={tourStep}
                        targetRect={tourRect}
                        stateName={node.state}
                        categoryOptions={categories}
                        initialExpiration={expirationDate || ""}
                        onSaveExpiration={handleTourSaveExpiration}
                        onNext={handleTourNext}
                        onSelectCategory={handleTourSelectCategory}
                        onClose={finishTour}
                    />
                )}
            </Box>
        </>
    );
}

/* ---------- helpers ---------- */
// Normalize to match requirement names to upload categories
function norm(s) {
    return String(s || "")
        .toLowerCase()
        .replace(/&/g, "and")
        .replace(/[^a-z0-9]+/g, "");
}

/**
 * Map requirements AND compute progress from uploads.
 * - "General CME" rightText becomes `${completedGeneral} / ${required}`
 * - For any specific requirement whose name matches an upload category,
 *   rightText becomes `${completedForThatCategory} / ${required}`
 * - If a requirement has variable credits (null), keep "Credits: variable"
 */
function mapRequirementsWithProgress(reqs, uploads) {
    let totalRequired = 0;

    // Sum credits by upload category
    const creditsByCat = uploads.reduce((acc, r) => {
        const key = norm(r.category);
        acc[key] = (acc[key] || 0) + Number(r.credits || 0);
        return acc;
    }, {});

    // Total completed for donut = sum of all uploaded credits
    const totalCompleted = uploads.reduce((s, r) => s + Number(r.credits || 0), 0);

    const GENERAL_KEY = norm("General CME");
    const isGeneralReq = (key) => key === GENERAL_KEY || key.startsWith(GENERAL_KEY);

    const list = reqs.map((r) => {
        const reqKey = norm(r.name);
        const completed = creditsByCat[reqKey] || 0;

        if (isGeneralReq(reqKey) && typeof r.credits_required === "number") {
            totalRequired = r.credits_required;
        }

        if (r.credits_required == null) {
            return {
                name: r.name,
                frequency: r.frequency || null,
                linkText: r.link ? "Obtain Course" : null,
                linkHref: r.link || null,
                rightText: "Credits: variable",
                info: r.info || null,
            };
        } else {
            return {
                name: r.name,
                frequency: r.frequency || null,
                linkText: r.link ? "Obtain Course" : null,
                linkHref: r.link || null,
                rightText: `${completed} / ${r.credits_required} credits`,
                info: r.info || null,
            };
        }
    });

    // For states without a General CME requirement, sum all numeric requirements
    if (totalRequired === 0) {
        totalRequired = reqs.reduce((sum, r) => sum + (typeof r.credits_required === "number" ? r.credits_required : 0), 0);
    }

    const visibleList = list.filter((_, i) => !isGeneralReq(norm(reqs[i].name)));

    return { reqCards: visibleList, totalRequired, totalCompleted };
}

function DonutProgress({ completed, total }) {
    const safeTotal = Math.max(total || 0, 1);
    const pct = Math.max(0, Math.min(100, (completed / safeTotal) * 100));
    return (
        <Box sx={{ display: "grid", placeItems: "center", height: { xs: 220, md: 260 } }}>
            <Box sx={{ position: "relative", display: "inline-flex" }}>
                <svg width="180" height="180" viewBox="0 0 36 36">
                    <path
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke="#eef1f5"
                        strokeWidth="3"
                    />
                    <path
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke={themeColors.teal}
                        strokeWidth="3"
                        strokeDasharray={`${pct}, 100`}
                    />
                </svg>
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center"
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {completed} / {total || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7, lineHeight: 1.2 }}>
                        credits
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7, lineHeight: 1.2 }}>
                        completed
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

function RequirementCard({ req }) {
    // `info` may be a plain string or an object { text, link, linkText } whose
    // tooltip renders a clickable link (MUI tooltips are interactive by default).
    const infoTitle =
        req.info && typeof req.info === "object" ? (
            <Box>
                {req.info.text && (
                    <Typography variant="body2" sx={{ mb: req.info.link ? 0.5 : 0 }}>
                        {req.info.text}
                    </Typography>
                )}
                {req.info.link && (
                    <Link
                        href={req.info.link}
                        target="_blank"
                        rel="noreferrer"
                        sx={{ color: "#fff", textDecorationColor: "#fff", fontSize: 13 }}
                    >
                        {req.info.linkText || req.info.link}
                    </Link>
                )}
            </Box>
        ) : (
            req.info
        );

    return (
        <Box sx={{ border: "1px solid #e8eaed", borderRadius: 2, p: 1.5, bgcolor: "#fff" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
                        <Typography sx={{ fontWeight: 700 }}>{req.name}</Typography>
                        {req.info && (
                            <Tooltip title={infoTitle} arrow placement="top">
                                <InfoOutlinedIcon sx={{ fontSize: 16, color: "#999", cursor: "pointer" }} />
                            </Tooltip>
                        )}
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {req.frequency && (
                            <Chip
                                size="small"
                                label={req.frequency}
                                sx={{
                                    bgcolor: "#edf7f7",
                                    color: themeColors.teal,
                                    borderRadius: 1,
                                    height: 22
                                }}
                            />
                        )}
                        {req.linkText && req.linkHref && (
                            <Link href={req.linkHref} target="_blank" rel="noreferrer" sx={{ fontSize: 13 }}>
                                {req.linkText}
                            </Link>
                        )}
                    </Stack>
                </Box>
                <Typography sx={{ fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>{req.rightText}</Typography>
            </Stack>
        </Box>
    );
}
